import { useEffect, useRef } from "preact/hooks";
import { nostojs } from "@nosto/nosto-js";

interface DisruptorData {
  image_desktop: string;
  image_mobile: string;
  image_alt?: string;
  text_color?: string;
  text_bg_color?: string;
  title?: string;
  body_text?: string;
  cta_label?: string;
  cta_url?: string;
  desktop_column_span?: 1 | 2;
  mobile_column_span?: 1 | 2;
  cta_style?: "primary" | "secondary";
}

interface NostoContent {
  [key: string]: {
    html: string;
    result_id: string;
  };
}

export const useDisruptors = ([altered, loading]: [boolean, boolean]) => {
  /**
   * Disruptor is created as an onsite content campaign
   * as an application/json
   *
   * @example
   * ```Template HTML/CSS
   * #set($imageDesktop = "")
   * #set($imageMobile = "")
   * #set($imageAlt = "This is the alt text")
   * #set($textColor = "#FFFFFF")
   * #set($textBgColor = "#000")
   * #set($title = "")
   * #set($bodyText = "")
   * #set($ctaLabel = "")
   * #set($ctaUrl = "#")
   * #set($ctaStyle = "primary")
   * #set($desktopColSpan = "2")
   * #set($mobileColSpan = "2")
   *
   * <script type="application/json" data-disruptor-json>
   *    {
   *      "image_desktop": "$imageDesktop",
   *      ...
   *    }
   * </script>
   * ```
   */
  const processedCampaigns = useRef<Record<string, any> | null>(null);
  const getDisruptorJSON = (html: string): DisruptorData | null => {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const script = doc.querySelector("script[data-disruptor-json]");
      return script ? JSON.parse(script.textContent || "{}") : null;
    } catch (e) {
      console.error("Failed to parse disruptor JSON", e);
      return null;
    }
  };

  const renderDisruptorHTML = (disruptor: DisruptorData | null) => {
    if (!disruptor) return "";

    const {
      image_desktop,
      image_mobile,
      image_alt = "",
      text_color = "#FFFFFF",
      text_bg_color = "transparent",
      title = "",
      body_text = "",
      cta_label = "",
      cta_url = "",
      desktop_column_span = 1,
      mobile_column_span = 1,
      cta_style = "primary",
    } = disruptor;

    const contentBgStyles =
      !title && !body_text && !cta_label
        ? `background-color: transparent;`
        : `background-color: ${text_bg_color};`;

    const classes = [
      desktop_column_span === 2 && "plp__grid-item--desktop-col-span-2",
      mobile_column_span === 2 && "plp__grid-item--mobile-col-span-2",
    ]
      .filter(Boolean)
      .join(" ");

    const ctaHtml =
      cta_label && cta_url
        ? `
        <button
          class="br-disruptor-tile__cta br-button br-button--${cta_style} br-button--handdrawn"
          onclick="goToPage(event,'${cta_url}')"
        >
          ${cta_label}
        </button>
      `
        : "";

    /* -------------------------
      Srcset
    ------------------------- */
    function withParams(url: string | URL, params = {}) {
      if (!url) return "";

      const u = new URL(url, window.location.origin);

      Object.entries(params).forEach(([key, value]) => {
        u.searchParams.set(key, value);
      });

      return u.toString();
    }

    function buildSrcSet(url: string | URL, widths: number[] = [], sizes = "") {
      if (!url || !widths.length) return "";

      const srcset = widths
        .map((w) => `${withParams(url, { width: w })} ${w}w`)
        .join(",\n");

      return `
      srcset="${srcset}"
      sizes="${sizes}"
    `;
    }

    let desktopImageSrcSet = "";

    if (desktop_column_span === 1) {
      desktopImageSrcSet = buildSrcSet(
        image_desktop,
        [318, 652],
        `
        (max-width: 2560px) 25vw,
        318px
      `,
      );
    }

    if (desktop_column_span === 2) {
      desktopImageSrcSet = buildSrcSet(
        image_desktop,
        [652, 1212],
        `
        (max-width: 2560px) 50vw,
        652px
      `,
      );
    }

    let mobileImageSrcSet = "";

    if (mobile_column_span === 1) {
      mobileImageSrcSet = buildSrcSet(
        image_mobile,
        [187, 374, 561],
        `
        (max-width: 768px) 50vw,
        (max-width: 1024px) 33vw,
        0
      `,
      );
    }

    if (mobile_column_span === 2) {
      mobileImageSrcSet = buildSrcSet(
        image_mobile,
        [382, 764, 1146],
        `
        (max-width: 768px) 100vw,
        (max-width: 1024px) 50vw,
        0
      `,
      );
    }

    return `
      <div
        class="plp__grid-item plp__grid-item--disruptor ${classes}"
      >
        <div class="skeleton">
          <div class="skeleton__image"></div>
          <div class="skeleton__text-container">
            <div class="skeleton__text"></div>
            <div class="skeleton__text"></div>
            <div class="skeleton__text"></div>
            <div class="skeleton__swatches"></div>
          </div>
        </div>
        <div 
          class="br-disruptor-tile"
          role="group"
          aria-label="disruptor tile"
          style="color: ${text_color} !important;"
        >
          ${
            cta_url
              ? `<a href="${cta_url}" class="br-disruptor-tile__link" style="color: ${text_color} !important;">`
              : ""
          }
          <div class="br-disruptor-tile__image-container">
            <img
              src="${image_desktop}"
              ${desktopImageSrcSet}
              alt="${image_alt}"
              class="md:block hidden"
            />
            <img
              src="${image_mobile}"
              ${mobileImageSrcSet}
              alt="${image_alt}"
              class="md:hidden"
            />
          </div>
          <div
            class="br-disruptor-tile__content"
            style="${contentBgStyles}"
          >
            <div class="w-full">
              ${title ? `<h4 class="mb-1">${title}</h4>` : ""}
              ${body_text ? `<p>${body_text}</p>` : ""}
            </div>
            ${ctaHtml}
          </div>
          ${cta_url ? "</a>" : ""}
        </div>
      </div>
    `;
  };

  useEffect(() => {
    if (loading) return;

    nostojs((api) => {
      if (altered) {
        api.placements.reset();
        return;
      }

      // Helper function to handle reinjection safely
      const inject = (campaigns: Record<string, any>) => {
        // Manual DOM injection fallback if Nosto's injectCampaigns fails on re-mount
        Object.entries(campaigns).forEach(([divId, item]) => {
          const targetEl = document.getElementById(divId);
          if (targetEl && item.html) {
            targetEl.innerHTML = item.html;
          }
        });

        api.placements.injectCampaigns(campaigns);
      };

      if (processedCampaigns.current) {
        inject(processedCampaigns.current);
        return;
      }

      // First-time load fetch
      api
        .defaultSession()
        .viewFrontPage()
        .setPlacements(api.placements.getPlacements())
        .load()
        .then(async (response: any) => {
          const content: NostoContent = response?.campaigns?.content;
          if (!content) return;

          const disruptorEntries = Object.entries(content).filter(([key]) =>
            key.toLowerCase().includes("disruptor"),
          );
          if (disruptorEntries.length === 0) return;

          const results = await Promise.all(
            disruptorEntries.map(async ([key, item]) => {
              const data = getDisruptorJSON(item.html);
              const html = renderDisruptorHTML(data);
              return [key, { div_id: key, html, result_id: item.result_id }];
            }),
          );

          const transformedEntries = Object.fromEntries(results);
          processedCampaigns.current = transformedEntries;

          inject(transformedEntries);
        });
    });
  }, [altered, loading]);
};
